import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  LinearProgress,
  Chip,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import axiosClient from "../../../api/AxiosClient";
import ReactMarkdown from "react-markdown";
import styles from "../agent-response.module.scss";

type Question = {
  question_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  is_required: boolean;
  help_text: string | null;
};

type RfqBuilderAgentProps = {
  parsedContent: any;
};

export default function RfqBuilderAgent({
  parsedContent,
}: RfqBuilderAgentProps): JSX.Element {
  console.log("====== RFQ BUILDER COMPONENT CALLED ======");
  console.log("RFQ Builder Agent - Parsed Content:", parsedContent);
  console.log("Type of parsedContent:", typeof parsedContent);
  console.log("parsedContent keys:", Object.keys(parsedContent || {}));
  console.log(
    "parsedContent stringified:",
    JSON.stringify(parsedContent, null, 2),
  );

  const dispatch = useAppDispatch();
  const params = new URLSearchParams(location.search);
  const sessionIdFromUrl = params.get("sessionId");

  // Get current agent info from Redux
  const { selectedAgent } = useSelector((state: RootState) => state.user);

  // Extract data from the response
  // userSlice processes rfq_builder through the generic handler which puts data in finalAnswer
  // parsedContent structure: { finalAnswer: { agent_id, status, all_questions, message, ... } }
  const result =
    parsedContent?.finalAnswer || parsedContent?.result?.data || parsedContent;

  console.log("Extracted result:", result);
  console.log("result stringified:", JSON.stringify(result, null, 2));
  console.log("All questions:", result?.all_questions);
  console.log("All questions length:", result?.all_questions?.length);

  const message = result?.message || "";
  const status = result?.status || "";
  const allQuestions: Question[] = result?.all_questions || [];
  const totalQuestions = result?.total_questions || allQuestions.length;
  const chatId = result?.chat_id || parsedContent?.chatId;
  const workflowId = result?.workflow_id || parsedContent?.workflowId;
  const sessionId = result?.session_id || result?.chat_id;
  const generatedDocumentPath = result?.generated_document_path || null;
  const downloadUrlFromBackend = result?.download_url || null;
  const structuredPreview = result?.structured_preview || null;
  const collectedData = result?.collected_data || {};

  console.log("Component state:", {
    status,
    questionsCount: allQuestions.length,
    sessionId,
    hasQuestions: allQuestions.length > 0,
    hasStructuredPreview: !!structuredPreview,
    collectedDataKeys: Object.keys(collectedData),
  });

  console.log("Structured Preview:", structuredPreview);
  console.log("Collected Data:", collectedData);

  // State management
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>(
    {},
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [editedData, setEditedData] = useState<Record<string, string>>({});
  const [isEditingField, setIsEditingField] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const currentQuestion = allQuestions[currentQuestionIndex];
  const currentAnswer = answers[currentQuestion?.question_id] || "";
  const currentCustomAnswer = customAnswers[currentQuestion?.question_id] || "";

  // Progress calculation
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;
  const answeredCount = Object.keys(answers).length;

  // Handlers
  const handleAnswerSelect = (questionId: string, value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    // Clear custom answer if switching away from "Other"
    if (!value.toLowerCase().includes("other") && customAnswers[questionId]) {
      setCustomAnswers((prev) => {
        const updated = { ...prev };
        delete updated[questionId];
        return updated;
      });
    }
  };

  const handleCustomAnswerChange = (questionId: string, value: string) => {
    setCustomAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < allQuestions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    }
  };

  const handleSubmitAllAnswers = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Format answers for submission
      const formattedAnswers: Record<string, string> = {};

      Object.keys(answers).forEach((questionId) => {
        const answer = answers[questionId];
        const customAnswer = customAnswers[questionId];

        // If "Other" was selected and there's a custom answer, use the custom answer
        if (answer.toLowerCase().includes("other") && customAnswer) {
          formattedAnswers[questionId] = customAnswer;
        } else {
          formattedAnswers[questionId] = answer;
        }
      });

      // Prepare the answer submission data
      const answerData = {
        action: "submit_answers",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        answers: formattedAnswers,
      };

      // Send via Redux - this will handle SSE streaming and add agent response to chat
      const timestamp = new Date().toISOString();
      const questionId = uuid();

      await dispatch(
        sendMessageToAgent({
          agentId: selectedAgent!.id,
          message: JSON.stringify(answerData),
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq-builder",
          sessionId: sessionIdFromUrl,
        }),
      ).unwrap();

      console.log("Answers submitted successfully");
      // Don't set submitSuccess - backend will respond with awaiting_confirmation status
    } catch (error: any) {
      console.error("Error submitting answers:", error);
      setSubmitError(
        error.message || "Failed to submit answers. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle document download
  // Handle confirmation of collected data
  const handleConfirmData = async () => {
    setIsConfirming(true);
    setSubmitError(null);

    try {
      // Merge edited data with collected data
      const finalData = {
        ...collectedData,
        ...editedData,
      };

      const confirmData = {
        action: "confirm_data",
        session_id: sessionId,
        chat_id: chatId,
        workflow_id: workflowId,
        collected_data: finalData,
      };

      const timestamp = new Date().toISOString();
      const questionId = uuid();

      await dispatch(
        sendMessageToAgent({
          agentId: selectedAgent!.id,
          message: JSON.stringify(confirmData),
          questionId,
          timestamp,
          agentEndpoint: "/stream/rfq-builder",
          sessionId: sessionIdFromUrl,
        }),
      ).unwrap();

      console.log("Data confirmed successfully");
    } catch (error: any) {
      console.error("Error confirming data:", error);
      setSubmitError(
        error.message || "Failed to confirm data. Please try again.",
      );
    } finally {
      setIsConfirming(false);
    }
  };

  // Handle field edit
  const handleFieldEdit = (fieldId: string, newValue: string) => {
    setEditedData((prev) => ({
      ...prev,
      [fieldId]: newValue,
    }));
  };

  const handleDownload = async () => {
    const filename = generatedDocumentPath
      ? generatedDocumentPath.split(/[\\\/]/).pop()
      : null;

    if (!filename) {
      alert("Download information is not available.");
      return;
    }

    // Determine download URL
    let urlToDownload = downloadUrlFromBackend;
    if (!urlToDownload && sessionId) {
      urlToDownload = `/rfq/download/${sessionId}`;
    }

    if (!urlToDownload) {
      alert("Download URL is not available. Please contact support.");
      return;
    }

    setIsDownloading(true);

    try {
      console.log("Downloading file from:", urlToDownload);

      const response = await axiosClient.get(urlToDownload, {
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("Download completed successfully");
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download the document. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const isCurrentQuestionAnswered = !!currentAnswer;
  const canSubmit = answeredCount === totalQuestions;

  // Show completion state (new message from backend with status="completed")
  if (status === "completed") {
    return (
      <div className={styles.pressReleaseContent}>
        <Paper
          elevation={0}
          sx={{
            padding: "32px",
            backgroundColor: "#f0fbf9",
            border: "2px solid #b2e5de",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 168, 136, 0.1)",
          }}
        >
          {message && (
            <Box
              sx={{
                fontSize: "15px",
                color: "#2a312b",
                marginBottom: 3,
                padding: "20px",
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #d0e8e4",
                "& p": { margin: "8px 0" },
                "& strong": { fontWeight: 600, color: "#004141" },
                "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
                "& li": { marginBottom: "4px" },
              }}
            >
              <ReactMarkdown>{message}</ReactMarkdown>
            </Box>
          )}

          {generatedDocumentPath && (
            <Box>
              <Button
                variant="contained"
                size="large"
                startIcon={
                  isDownloading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownload}
                disabled={
                  isDownloading || (!downloadUrlFromBackend && !sessionId)
                }
                sx={{
                  backgroundColor: "#00a888",
                  color: "#fff",
                  padding: "14px 32px",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  boxShadow: "0 4px 12px rgba(0, 168, 136, 0.3)",
                  width: "100%",
                  "&:hover": {
                    backgroundColor: "#008f75",
                    boxShadow: "0 6px 16px rgba(0, 168, 136, 0.4)",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                }}
              >
                {isDownloading ? "Downloading..." : "Download RFQ Document"}
              </Button>
            </Box>
          )}
        </Paper>
      </div>
    );
  }

  // Note: Removed intermediate success screen - backend immediately responds with awaiting_confirmation

  // Show data preview and confirmation state
  console.log("Checking awaiting_confirmation condition:", {
    status,
    hasStructuredPreview: !!structuredPreview,
    structuredPreviewSections: structuredPreview?.sections?.length,
    hasCollectedData: Object.keys(collectedData).length > 0,
  });

  if (status === "awaiting_confirmation") {
    // If no structured preview, create a basic one from collected data
    const displayPreview = structuredPreview || {
      sections: [
        {
          section_id: "all_answers",
          section_title: "All Collected Answers",
          fields: Object.entries(collectedData).map(([key, value]) => ({
            field_id: key,
            field_label: key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l: string) => l.toUpperCase()),
            field_value: value as string,
            editable: true,
          })),
        },
      ],
    };
    return (
      <div className={styles.pressReleaseContent}>
        {/* Header Message */}
        {message && (
          <Paper
            elevation={0}
            sx={{
              padding: "20px",
              marginBottom: "20px",
              backgroundColor: "#f0fbf9",
              border: "1px solid #b2e5de",
              borderRadius: "8px",
            }}
          >
            <Box
              sx={{
                fontSize: "15px",
                fontWeight: 500,
                color: "#004141",
                "& p": { margin: "8px 0" },
                "& strong": { fontWeight: 600 },
                "& ul, & ol": { marginLeft: "20px", marginTop: "8px" },
                "& li": { marginBottom: "4px" },
              }}
            >
              <ReactMarkdown>{message}</ReactMarkdown>
            </Box>
          </Paper>
        )}

        {/* Structured Preview with Sections */}
        <Box sx={{ marginBottom: 4 }}>
          {displayPreview.sections?.map(
            (section: any, sectionIndex: number) => {
              console.log(
                `Rendering section ${sectionIndex}:`,
                section.section_title,
                `with ${section.fields?.length} fields`,
              );
              return (
                <Accordion
                  key={section.section_id}
                  defaultExpanded={true}
                  sx={{
                    marginBottom: 3,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    border: "1px solid #d0e8e4",
                    "&:before": { display: "none" },
                    borderRadius: "8px !important",
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#00a888" }} />}
                    sx={{
                      backgroundColor: "#f0fbf9",
                      borderRadius: "8px 8px 0 0",
                      minHeight: "56px",
                      "& .MuiAccordionSummary-content": {
                        margin: "16px 0",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "17px",
                        fontWeight: 600,
                        color: "#004141",
                        letterSpacing: "0.2px",
                      }}
                    >
                      {section.section_title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={{
                      padding: "24px 24px 28px 24px",
                      backgroundColor: "#fafcfb",
                    }}
                  >
                    {section.fields?.map((field: any, fieldIndex: number) => {
                      const currentValue =
                        editedData[field.field_id] ||
                        collectedData[field.field_id] ||
                        field.field_value;
                      const isEditing = isEditingField === field.field_id;

                      return (
                        <Box
                          key={field.field_id}
                          sx={{
                            marginBottom:
                              fieldIndex < section.fields.length - 1 ? 4 : 0,
                            paddingBottom:
                              fieldIndex < section.fields.length - 1 ? 3 : 0,
                            borderBottom:
                              fieldIndex < section.fields.length - 1
                                ? "1px solid #f0f0f0"
                                : "none",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: 1.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#004141",
                                flex: 1,
                                lineHeight: 1.5,
                              }}
                            >
                              {field.field_label}
                            </Typography>
                            {field.editable && !isEditing && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setIsEditingField(field.field_id)
                                }
                                sx={{
                                  color: "#00a888",
                                  padding: "6px",
                                  backgroundColor: "#e0f2f1",
                                  "&:hover": {
                                    backgroundColor: "#b2dfdb",
                                  },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            )}
                          </Box>

                          {isEditing ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                              }}
                            >
                              <TextField
                                fullWidth
                                value={currentValue}
                                onChange={(e) =>
                                  handleFieldEdit(
                                    field.field_id,
                                    e.target.value,
                                  )
                                }
                                size="small"
                                autoFocus
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#00a888",
                                    },
                                  },
                                }}
                              />
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => setIsEditingField(null)}
                                sx={{
                                  backgroundColor: "#00a888",
                                  minWidth: "60px",
                                  "&:hover": {
                                    backgroundColor: "#008f75",
                                  },
                                }}
                              >
                                Done
                              </Button>
                            </Box>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: "15px",
                                color: "#2a312b",
                                backgroundColor: "#f9fafb",
                                padding: "14px 16px",
                                borderRadius: "6px",
                                border: "1px solid #e0e7ee",
                                lineHeight: 1.6,
                                fontWeight: 500,
                              }}
                            >
                              {currentValue}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            },
          )}
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            marginTop: 4,
            paddingTop: 3,
            borderTop: "1px solid #e0e7ee",
          }}
        >
          <Button
            variant="contained"
            onClick={handleConfirmData}
            disabled={isConfirming}
            startIcon={
              isConfirming ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )
            }
            sx={{
              backgroundColor: "#00a888",
              color: "#fff",
              padding: "12px 32px",
              fontSize: "15px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(0, 168, 136, 0.3)",
              "&:hover": {
                backgroundColor: "#008f75",
                boxShadow: "0 4px 12px rgba(0, 168, 136, 0.4)",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {isConfirming ? "Confirming..." : "Confirm & Generate Document"}
          </Button>
        </Box>

        {/* Error Message */}
        {submitError && (
          <Paper
            elevation={0}
            sx={{
              padding: 2,
              marginTop: 2,
              backgroundColor: "#ffebee",
              border: "1px solid #ef9a9a",
              borderRadius: "8px",
            }}
          >
            <Typography sx={{ fontSize: "14px", color: "#c62828" }}>
              {submitError}
            </Typography>
          </Paper>
        )}
      </div>
    );
  }

  // Show loading or no questions state
  if (allQuestions.length === 0) {
    return (
      <div className={styles.pressReleaseContent}>
        <Paper
          elevation={0}
          sx={{
            padding: 3,
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#403f3f" }}>
            {message || "Loading questions..."}
          </Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div className={styles.pressReleaseContent}>
      {/* Header Message */}
      {message && (
        <Paper
          elevation={0}
          sx={{
            padding: "20px",
            marginBottom: "20px",
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 500,
              color: "#004141",
            }}
          >
            {message}
          </Typography>
        </Paper>
      )}

      {/* Progress Bar */}
      <Box sx={{ marginBottom: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 1,
          }}
        >
          <Typography
            sx={{ fontSize: "14px", fontWeight: 600, color: "#004141" }}
          >
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </Typography>
          <Chip
            label={`${answeredCount}/${totalQuestions} answered`}
            size="small"
            sx={{
              backgroundColor: "#e0f2f1",
              color: "#004141",
              fontWeight: 600,
            }}
          />
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: 4,
            backgroundColor: "#e0f2f1",
            "& .MuiLinearProgress-bar": {
              backgroundColor: "#00a888",
              borderRadius: 4,
            },
          }}
        />
      </Box>

      {/* Question Card */}
      {currentQuestion && (
        <Paper
          elevation={0}
          sx={{
            padding: 3,
            marginBottom: 3,
            border: "1px solid #b2e5de",
            borderRadius: "8px",
          }}
        >
          {/* Question Text */}
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#004141",
              marginBottom: 1,
            }}
          >
            {currentQuestion.question_text}
            {currentQuestion.is_required && (
              <span style={{ color: "#c62828", marginLeft: 4 }}>*</span>
            )}
          </Typography>

          {/* Help Text */}
          {currentQuestion.help_text && (
            <Typography
              sx={{
                fontSize: "13px",
                color: "#666",
                marginBottom: 2,
                fontStyle: "italic",
              }}
            >
              {currentQuestion.help_text}
            </Typography>
          )}

          {/* Question Type: Select (Radio) */}
          {currentQuestion.question_type === "select" && (
            <RadioGroup
              value={currentAnswer}
              onChange={(e) =>
                handleAnswerSelect(currentQuestion.question_id, e.target.value)
              }
            >
              {currentQuestion.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={
                    <Radio
                      sx={{
                        color: "#00a888",
                        "&.Mui-checked": {
                          color: "#00a888",
                        },
                      }}
                    />
                  }
                  label={option}
                  sx={{
                    marginBottom: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      color: "#2a312b",
                    },
                  }}
                />
              ))}
            </RadioGroup>
          )}

          {/* Custom Answer Input (for "Other" option) */}
          {currentAnswer.toLowerCase().includes("other") && (
            <Box sx={{ marginTop: 2 }}>
              <TextField
                fullWidth
                label="Please specify"
                value={currentCustomAnswer}
                onChange={(e) =>
                  handleCustomAnswerChange(
                    currentQuestion.question_id,
                    e.target.value,
                  )
                }
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#00a888",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#00a888",
                  },
                }}
              />
            </Box>
          )}

          {/* Question Type: Text */}
          {currentQuestion.question_type === "text" && (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={currentAnswer}
              onChange={(e) =>
                handleAnswerSelect(currentQuestion.question_id, e.target.value)
              }
              variant="outlined"
              placeholder="Enter your answer here..."
              sx={{
                marginTop: 2,
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "#00a888",
                  },
                },
              }}
            />
          )}
        </Paper>
      )}

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={currentQuestionIndex === 0}
          sx={{
            color: "#004141",
            borderColor: "#b2e5de",
            "&:hover": {
              borderColor: "#00a888",
              backgroundColor: "#f0fbf9",
            },
            "&.Mui-disabled": {
              borderColor: "#e0e0e0",
              color: "#9e9e9e",
            },
          }}
        >
          Back
        </Button>

        {currentQuestionIndex < allQuestions.length - 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
            sx={{
              backgroundColor: "#00a888",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#008f75",
              },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmitAllAnswers}
            disabled={!canSubmit || isSubmitting}
            sx={{
              backgroundColor: canSubmit ? "#00a888" : "#9e9e9e",
              color: "#fff",
              "&:hover": {
                backgroundColor: canSubmit ? "#008f75" : "#9e9e9e",
              },
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0",
                color: "#9e9e9e",
              },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit All Answers"}
          </Button>
        )}
      </Box>

      {/* Submit Error */}
      {submitError && (
        <Paper
          elevation={0}
          sx={{
            padding: 2,
            marginTop: 2,
            backgroundColor: "#ffebee",
            border: "1px solid #ef9a9a",
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#c62828" }}>
            {submitError}
          </Typography>
        </Paper>
      )}

      {/* Warning if not all questions answered */}
      {!canSubmit && currentQuestionIndex === allQuestions.length - 1 && (
        <Paper
          elevation={0}
          sx={{
            padding: 2,
            marginTop: 2,
            backgroundColor: "#fff3e0",
            border: "1px solid #ffb74d",
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#e65100" }}>
            Please answer all questions before submitting. You have answered{" "}
            {answeredCount} out of {totalQuestions} questions.
          </Typography>
        </Paper>
      )}
    </div>
  );
}
